import { useCallback } from 'react'
import { TSetStatus } from '~components/molecules/animated-alert/animated-alert-provider'
import { useDownloadBulkCertificates } from '~hooks/queries/documents/use-download-bulk-certificates'
import { useDownloadCertificate } from '~hooks/queries/documents/use-download-certificate'
import { useGetCertificateLink } from '~hooks/queries/documents/use-get-certificate-link'
import { IDocumentsRowData } from '~hooks/queries/vessels/documents/types'
import { gtm } from '~utils/gtm'

const copyLinkToClipboard = async (
  url: string,
  setStatusAnimatedAlert: TSetStatus,
) => {
  const cb = navigator.clipboard
  await cb.writeText(url).then(() => {
    setStatusAnimatedAlert('Link copied to clipboard', 3000, 'success')
    gtm.pushEvent('document_package_copied')
  })
}

export function useDocumentDownloads(vesselId: string, versionId?: string) {
  const downloadCertificateMutation = useDownloadCertificate()
  const downloadBulkMutation = useDownloadBulkCertificates()
  const getCertificateLinkMutation = useGetCertificateLink()

  const getFileLink = useCallback(
    async (document: IDocumentsRowData): Promise<string | undefined> => {
      const fileName = document?.files?.[0]?.name || document?.fileName
      if (!fileName) return undefined

      const result = await getCertificateLinkMutation.mutateAsync({
        vesselId,
        filename: fileName,
        versionId: versionId ? versionId : undefined,
      })

      return result?.signedUrl
    },
    [vesselId, versionId, getCertificateLinkMutation],
  )

  const downloadSingleFile = useCallback(
    async (
      document: IDocumentsRowData,
      setStatusAnimatedAlert: TSetStatus,
      onlyCopyLink?: boolean,
    ): Promise<boolean> => {
      const fileName = document?.files?.[0]?.name || document?.fileName
      if (!fileName) return false

      try {
        const result = await downloadCertificateMutation.mutateAsync({
          vesselId,
          filename: fileName,
          versionId: versionId ? versionId : undefined,
        })

        if (result.signedUrl) {
          if (onlyCopyLink) {
            await copyLinkToClipboard(result.signedUrl, setStatusAnimatedAlert)
          } else {
            gtm.pushEvent('document_downloaded', {
              document_name: document.documentName,
              document_category: document.type,
            })
            window.location.href = result.signedUrl
          }
          setStatusAnimatedAlert(
            'Document downloaded successfully.',
            undefined,
            'success',
          )
          return true
        } else {
          setStatusAnimatedAlert(
            'An Error occurred when opening the certificate.',
            undefined,
            'error',
          )
          return false
        }
      } catch {
        setStatusAnimatedAlert(
          'An Error occurred when opening the certificate.',
          undefined,
          'error',
        )
        return false
      }
    },
    [vesselId, versionId, downloadCertificateMutation],
  )

  const downloadBulkFiles = useCallback(
    async (
      fileNames: string[],
      setStatusAnimatedAlert: TSetStatus,
      onlyCopyLink?: boolean,
    ): Promise<boolean> => {
      try {
        const result = await downloadBulkMutation.mutateAsync({
          vesselId,
          fileNames,
        })

        if (result.signedUrl) {
          if (onlyCopyLink) {
            await copyLinkToClipboard(result.signedUrl, setStatusAnimatedAlert)
          } else {
            window.location.href = result.signedUrl
            setStatusAnimatedAlert(
              'A ready-to-go zip file has been added to your download folder on your computer',
              3000,
              'success',
            )
            gtm.pushEvent('document_package_downloaded')
          }
          return true
        }

        return false
      } catch {
        setStatusAnimatedAlert(
          'An Error occurred when downloading files.',
          undefined,
          'error',
        )
        return false
      }
    },
    [vesselId, versionId, downloadBulkMutation],
  )

  const downloadDocuments = useCallback(
    async (
      documents: IDocumentsRowData[],
      setStatusAnimatedAlert: TSetStatus,
      onlyCopyLink?: boolean,
    ): Promise<boolean> => {
      if (documents.length === 1 && !documents[0].files) {
        return await downloadSingleFile(
          documents[0],
          setStatusAnimatedAlert,
          onlyCopyLink,
        )
      } else if (documents.length === 1 && documents[0].files?.length === 1) {
        return await downloadSingleFile(
          documents[0],
          setStatusAnimatedAlert,
          onlyCopyLink,
        )
      } else {
        const fileNames = documents
          .map((document) => document.files)
          .flat(1)
          .filter((file) => file !== undefined)
          .map((file) => file.name)

        if (fileNames.length === 0) {
          setStatusAnimatedAlert(
            'No valid files found for download.',
            undefined,
            'error',
          )
          return false
        }

        return await downloadBulkFiles(
          fileNames,
          setStatusAnimatedAlert,
          onlyCopyLink,
        )
      }
    },
    [downloadSingleFile, downloadBulkFiles],
  )

  return {
    downloadDocuments,
    getFileLink,
    isDownloading:
      downloadCertificateMutation.isPending || downloadBulkMutation.isPending,
    isGettingLink: getCertificateLinkMutation.isPending,
  }
}
